// src/components/lp/LogoSlider.tsx
import React, { useRef, useEffect, useState } from 'react';
import { 
    Box, 
    Typography 
} from '@mui/material';
import { motion } from 'framer-motion';
import { styled } from '@mui/system';

// ロゴ画像のパスの配列
const logos = [
    '/images/MediaLogo/antenna ロゴ.png',
    '/images/MediaLogo/ASCII スタートアップロゴ.svg',
    '/images/MediaLogo/Biglobeニュースロゴ.png',
    '/images/MediaLogo/BtoBプラットフォームロゴ.jpeg',
    '/images/MediaLogo/infoseek　ロゴ.svg',
    '/images/MediaLogo/iza ロゴ.svg',
    '/images/MediaLogo/JBpressロゴ.svg',
    '/images/MediaLogo/jiji.comロゴ.svg',
    '/images/MediaLogo/mapionロゴ.gif',
    '/images/MediaLogo/NEWS picksロゴ.png',
    '/images/MediaLogo/PR Times ロゴ.png',
    '/images/MediaLogo/PRESIDENT Onlineロゴ.svg',
    '/images/MediaLogo/RBBtodayロゴ.png',
    '/images/MediaLogo/seo toolsロゴ.gif',
    '/images/MediaLogo/tbsnewsロゴ.svg',
    '/images/MediaLogo/technoedge.png',
    '/images/MediaLogo/エキサイトニュースロゴ.png',
    '/images/MediaLogo/ジョルダンニュースロゴ.jpeg',
    '/images/MediaLogo/ストプレロゴ.svg',
    '/images/MediaLogo/テレ東プラスロゴ.jpeg',
    '/images/MediaLogo/ニコニコニュースロゴ.png',
    '/images/MediaLogo/ホテリエロゴ.png',
    '/images/MediaLogo/ライブドアニュースロゴ.png',
    '/images/MediaLogo/宮崎日新聞ロゴ.png',
    '/images/MediaLogo/日経ロゴ.png',
    '/images/MediaLogo/東洋経済オンラインロゴ.png',
    '/images/MediaLogo/琉球新報ロゴ.svg',
    '/images/MediaLogo/石垣経済新聞ロゴ.png',
];

const SliderContainer = styled(Box)(({ theme }) => ({
    width: '100%',
    overflow: 'hidden', // 各スライダーが縦に並ぶので、コンテナのoverflowはhiddenのままで良い
    padding: theme.spacing(2, 0),
}));

const SliderTrack = styled(motion.div)({
    display: 'block', // 各スライダーを新しい行に表示
    whiteSpace: 'nowrap', // スライダー内のロゴは横に並べる
    marginBottom: '20px', // 各スライダー間のスペース
});

const LogoImage = styled('img')({
    height: '50px', // ロゴの高さ
    margin: '0 20px', // ロゴ間のスペース
    verticalAlign: 'middle',
});

const LogoSlider: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [imageBlocks, setImageBlocks] = useState(logos);

    // ロゴの幅とマージンを考慮したアイテムの合計幅
    const itemWidthWithGap = 50 + 20 * 2; // height: 50px, margin: 0 20px

    // 初期コンテンツ数
    const numberOfContents = logos.length;

    useEffect(() => {
        // 「横幅の穴埋め」
        // windowの長さよりコンテンツ数が少ない場合
        // 横幅 < 画像の合計の長さ となるように、画像群(シーケンス)をループさせて配列に加える
        if (
            ref.current?.offsetWidth &&
            imageBlocks.length * itemWidthWithGap < ref.current.offsetWidth
        ) {
            // 全体の長さから何個分　足りていないのか
            const fillableNumberOfContents: number = Math.floor(
                (ref.current.offsetWidth - imageBlocks.length * itemWidthWithGap) /
                itemWidthWithGap
            );

            // シーケンスを追加するのは何個か
            const fillableNumberOfSequence: number = Math.ceil(
                fillableNumberOfContents / numberOfContents
            );

            // シーケンス分　コンテンツを追加
            const newImageBlocks = [...imageBlocks];
            for (let i = 0; i < fillableNumberOfSequence; i++) {
                newImageBlocks.push(...logos);
            }
            setImageBlocks(newImageBlocks);
        }
    }, [ref.current, imageBlocks.length, itemWidthWithGap, numberOfContents]); //DOMがレンダリングされ、横幅が確定した瞬間に実行される

    const sliderVariants = (initialOffset: number) => ({
        animate: {
            x: [`${initialOffset / 2 }px`, `-${(imageBlocks.length * itemWidthWithGap) / 8 + initialOffset}px`], // 左に移動し、オフセットを考慮
            transition: {
                x: {
                    repeat: Infinity,
                    repeatType: 'loop',
                    duration: imageBlocks.length * 2, // スライド速度をコンテンツ数に比例させる
                    ease: 'linear',
                },
            },
        },
    });

    // 各スライダーの初期オフセットを計算
    const totalLogosWidth = numberOfContents * itemWidthWithGap;
    const offset1 = 0;
    const offset2 = totalLogosWidth / 3;
    const offset3 = (totalLogosWidth / 3) * 2;

    return (
        <Box sx={{
            width: '100%',
            maxWidth: '1200px',
            }}>
            <Typography 
                variant="h3" 
                align="center" 
                sx={{ marginBottom: 2 }}>
                多数のメディアで紹介されています！
            </Typography>
            <SliderContainer ref={ref}>
                {/* 1列目 */}
                <SliderTrack
                    variants={sliderVariants(offset1)}
                    animate="animate"
                >
                    {imageBlocks.map((logo, index) => (
                        <LogoImage key={`slider1-${index}`} src={logo} alt={`Logo ${index + 1}`} />
                    ))}
                </SliderTrack>
                {/* 2列目 */}
                <SliderTrack
                    variants={sliderVariants(offset2)}
                    animate="animate"
                >
                    {imageBlocks.map((logo, index) => (
                        <LogoImage key={`slider2-${index}`} src={logo} alt={`Logo ${index + 1}`} />
                    ))}
                </SliderTrack>
                {/* 3列目 */}
                <SliderTrack
                    variants={sliderVariants(offset3)}
                    animate="animate"
                >
                    {imageBlocks.map((logo, index) => (
                        <LogoImage key={`slider3-${index}`} src={logo} alt={`Logo ${index + 1}`} />
                    ))}
                </SliderTrack>
            </SliderContainer>
        </Box >
    );
};

export default LogoSlider;
